/**
 * Theme-System auf Basis von CSS-Custom-Properties.
 * Ein Theme liefert einen flachen Satz Design-Tokens (CSS-Variablenname -> Wert),
 * der auf Beamer- und Teilnehmeransicht konsistent angewendet wird.
 *
 * Bewusst projektionstauglich: hohe Kontraste, sattes Farbsystem für die
 * Antwortkacheln (Farbe wird IMMER mit einer eigenen Form/Symbol kombiniert,
 * daher nie alleiniger Bedeutungsträger).
 */

export interface Theme {
  id: string;
  name: string;
  description: string;
  /** Ist der Hintergrund hell? (steuert z. B. Overlay-Töne im Client) */
  light?: boolean;
  /** Bewegter Verlaufshintergrund (sanft animiert; respektiert reduced-motion). */
  animatedBg?: boolean;
  tokens: Record<string, string>;
}

// Sechs Antwortkachel-Farben je Theme; Textfarbe darauf über --opt-fg.
type Opt6 = [string, string, string, string, string, string];
function opt(colors: Opt6): Record<string, string> {
  return {
    '--opt-1': colors[0],
    '--opt-2': colors[1],
    '--opt-3': colors[2],
    '--opt-4': colors[3],
    '--opt-5': colors[4],
    '--opt-6': colors[5],
  };
}

export const THEMES: Theme[] = [
  {
    id: 'amt',
    name: 'Amt',
    description: 'Sachlich und ruhig – für den behördlichen Kontext.',
    tokens: {
      '--bg': 'radial-gradient(1200px 800px at 50% -10%, #14294a, #0b1524)',
      '--fg': '#eaf1fb',
      '--muted': '#9db2cd',
      '--surface': '#16273c',
      '--surface-2': '#1f3350',
      '--border': 'rgba(255,255,255,0.10)',
      '--accent': '#3b7ff5',
      '--accent-fg': '#ffffff',
      '--correct': '#34c27a',
      '--wrong': '#e2593f',
      '--radius': '0.9rem',
      '--opt-fg': '#ffffff',
      '--font-display': "'Segoe UI', system-ui, -apple-system, Roboto, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#3b6fe0', '#0f9d8f', '#6b5bd0', '#b5842e', '#b5495b', '#4a8c3f']),
    },
  },
  {
    id: 'kontrast',
    name: 'Kontrast',
    description: 'Maximaler Kontrast – auch auf schwachen Beamern lesbar.',
    tokens: {
      '--bg': '#000000',
      '--fg': '#ffffff',
      '--muted': '#d4d4d4',
      '--surface': '#121212',
      '--surface-2': '#1e1e1e',
      '--border': 'rgba(255,255,255,0.28)',
      '--accent': '#ffd400',
      '--accent-fg': '#000000',
      '--correct': '#00e676',
      '--wrong': '#ff453a',
      '--radius': '0.5rem',
      '--opt-fg': '#ffffff',
      '--font-display': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#d0021b', '#1554d6', '#0a8f6f', '#7b2ff7', '#c1188a', '#d1580a']),
    },
  },
  {
    id: 'frisch',
    name: 'Frisch',
    description: 'Lebendiges Grün-Türkis – wach und freundlich.',
    tokens: {
      '--bg': 'linear-gradient(160deg, #063a35 0%, #075e52 60%, #0a7f63 100%)',
      '--fg': '#eafff8',
      '--muted': '#a9d8cd',
      '--surface': 'rgba(255,255,255,0.10)',
      '--surface-2': 'rgba(255,255,255,0.16)',
      '--border': 'rgba(255,255,255,0.16)',
      '--accent': '#22d3a6',
      '--accent-fg': '#04231d',
      '--correct': '#7bf1a8',
      '--wrong': '#ff8a6b',
      '--radius': '1.1rem',
      '--opt-fg': '#04231d',
      '--font-display': "'Trebuchet MS', system-ui, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#38d9a9', '#4dabf7', '#ffd43b', '#ff8787', '#b197fc', '#ffa94d']),
    },
  },
  {
    id: 'sonne',
    name: 'Sonne',
    description: 'Warmer Sonnenverlauf – energiegeladen für Einstiege.',
    tokens: {
      '--bg': 'linear-gradient(155deg, #3a0f52 0%, #8a2350 55%, #c85a2b 100%)',
      '--fg': '#fff4ec',
      '--muted': '#f0c9bd',
      '--surface': 'rgba(255,255,255,0.12)',
      '--surface-2': 'rgba(255,255,255,0.18)',
      '--border': 'rgba(255,255,255,0.18)',
      '--accent': '#ff9f43',
      '--accent-fg': '#3a1200',
      '--correct': '#ffe066',
      '--wrong': '#ff6b6b',
      '--radius': '1.25rem',
      '--opt-fg': '#2b0f00',
      '--font-display': "'Trebuchet MS', system-ui, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#ff922b', '#ffd43b', '#ff6b6b', '#f783ac', '#a9e34b', '#4dd4c4']),
    },
  },
  {
    id: 'nacht',
    name: 'Nacht',
    description: 'Elegantes Dunkelviolett – ruhig und hochwertig.',
    tokens: {
      '--bg': 'radial-gradient(1200px 700px at 80% -20%, #241850, #0a0a16)',
      '--fg': '#efeaff',
      '--muted': '#a7a1c8',
      '--surface': '#16162a',
      '--surface-2': '#20203a',
      '--border': 'rgba(255,255,255,0.10)',
      '--accent': '#8b5cf6',
      '--accent-fg': '#ffffff',
      '--correct': '#4ade80',
      '--wrong': '#fb7185',
      '--radius': '1rem',
      '--opt-fg': '#ffffff',
      '--font-display': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#7c6cf0', '#4dabf7', '#e64980', '#20c997', '#f59f00', '#ff6b6b']),
    },
  },
  {
    id: 'papier',
    name: 'Papier',
    description: 'Hell und aufgeräumt – für gut beleuchtete Räume.',
    light: true,
    tokens: {
      '--bg': '#f5f7fb',
      '--fg': '#13233c',
      '--muted': '#5a6b82',
      '--surface': '#ffffff',
      '--surface-2': '#eef2f8',
      '--border': 'rgba(15,30,55,0.12)',
      '--accent': '#2563eb',
      '--accent-fg': '#ffffff',
      '--correct': '#16a34a',
      '--wrong': '#dc2626',
      '--radius': '0.9rem',
      '--opt-fg': '#ffffff',
      '--font-display': "'Segoe UI', system-ui, -apple-system, Roboto, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#e03131', '#1971c2', '#f08c00', '#2f9e44', '#7048e8', '#0c8599']),
    },
  },
  {
    id: 'klar',
    name: 'Klar',
    description: 'Klares, helles Apple-Design – ruhig, modern und aufgeräumt.',
    light: true,
    tokens: {
      '--bg': 'radial-gradient(1200px 800px at 50% -20%, #ffffff, #f5f5f7)',
      '--fg': '#1d1d1f',
      '--muted': '#6e6e73',
      '--surface': '#ffffff',
      '--surface-2': '#f1f1f4',
      '--border': 'rgba(0,0,0,0.08)',
      '--accent': '#0071e3',
      '--accent-fg': '#ffffff',
      '--correct': '#34c759',
      '--wrong': '#ff3b30',
      '--radius': '1.1rem',
      '--opt-fg': '#ffffff',
      '--font-display': "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
      '--font-body': "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",
      ...opt(['#e5484d', '#0071e3', '#8b5cf6', '#059669', '#c2410c', '#0891b2']),
    },
  },
  {
    id: 'neon',
    name: 'Neon',
    description: 'Dunkles Neon mit bewegtem Verlauf – maximal dynamisch.',
    animatedBg: true,
    tokens: {
      '--bg': 'linear-gradient(120deg, #10022e, #2b0a5e, #06304f, #14033a)',
      '--fg': '#f2eaff',
      '--muted': '#b4a5d6',
      '--surface': 'rgba(255,255,255,0.08)',
      '--surface-2': 'rgba(255,255,255,0.14)',
      '--border': 'rgba(255,255,255,0.16)',
      '--accent': '#22e0ff',
      '--accent-fg': '#04121a',
      '--correct': '#39ff88',
      '--wrong': '#ff4d9d',
      '--radius': '1.1rem',
      '--opt-fg': '#04121a',
      '--font-display': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#22e0ff', '#ff4d9d', '#f9f871', '#7cff6b', '#b388ff', '#ff9142']),
    },
  },
  {
    id: 'ozean',
    name: 'Ozean',
    description: 'Frische Blautöne mit bewegtem Verlauf.',
    animatedBg: true,
    tokens: {
      '--bg': 'linear-gradient(120deg, #012a4a, #013a63, #01497c, #014f86)',
      '--fg': '#eaf6ff',
      '--muted': '#a6c8e6',
      '--surface': 'rgba(255,255,255,0.10)',
      '--surface-2': 'rgba(255,255,255,0.16)',
      '--border': 'rgba(255,255,255,0.16)',
      '--accent': '#48cae4',
      '--accent-fg': '#012a4a',
      '--correct': '#80ffdb',
      '--wrong': '#ff8fa3',
      '--radius': '1.1rem',
      '--opt-fg': '#012235',
      '--font-display': "'Trebuchet MS', system-ui, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#48cae4', '#00b4d8', '#90e0ef', '#ffd166', '#f4978e', '#b5e48c']),
    },
  },
  {
    id: 'bonbon',
    name: 'Bonbon',
    description: 'Verspielt und knallig – für viel gute Laune.',
    animatedBg: true,
    tokens: {
      '--bg': 'linear-gradient(120deg, #7b2ff7, #c651d4, #f761a1, #ff9a5a)',
      '--fg': '#fff5fb',
      '--muted': '#ffd6ec',
      '--surface': 'rgba(255,255,255,0.16)',
      '--surface-2': 'rgba(255,255,255,0.24)',
      '--border': 'rgba(255,255,255,0.22)',
      '--accent': '#ffe14d',
      '--accent-fg': '#5a1548',
      '--correct': '#a0f5b0',
      '--wrong': '#ffd0d0',
      '--radius': '1.4rem',
      '--opt-fg': '#3a0f34',
      '--font-display': "'Trebuchet MS', system-ui, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#ff7ab6', '#ffd43b', '#8ce99a', '#74c0fc', '#b197fc', '#ffa94d']),
    },
  },
  {
    id: 'wald',
    name: 'Wald',
    description: 'Erdig-grün, ruhig und natürlich.',
    tokens: {
      '--bg': 'radial-gradient(1100px 700px at 30% -10%, #1e3a2b, #0c1a12)',
      '--fg': '#eafbf0',
      '--muted': '#a7c9b3',
      '--surface': '#16281d',
      '--surface-2': '#1f3728',
      '--border': 'rgba(255,255,255,0.10)',
      '--accent': '#7cc96b',
      '--accent-fg': '#0c1a12',
      '--correct': '#95e06c',
      '--wrong': '#e88f6a',
      '--radius': '0.9rem',
      '--opt-fg': '#ffffff',
      '--font-display': "'Segoe UI', system-ui, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#4a8c3f', '#2f9e8f', '#b5842e', '#8a6d3b', '#a0538b', '#5f8bd0']),
    },
  },
  {
    id: 'retro',
    name: 'Retro',
    description: 'Synthwave-Vibe – kräftig und nostalgisch.',
    animatedBg: true,
    tokens: {
      '--bg': 'linear-gradient(120deg, #2b1055, #7597de, #ff6a88, #ff9a44)',
      '--fg': '#fdf0ff',
      '--muted': '#e3c7f0',
      '--surface': 'rgba(20,10,40,0.35)',
      '--surface-2': 'rgba(20,10,40,0.5)',
      '--border': 'rgba(255,255,255,0.18)',
      '--accent': '#ff5da2',
      '--accent-fg': '#1a0530',
      '--correct': '#5ef2c7',
      '--wrong': '#ffb26b',
      '--radius': '0.8rem',
      '--opt-fg': '#ffffff',
      '--font-display': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#ff5da2', '#5d8bff', '#ffd166', '#5ef2c7', '#c77dff', '#ff8c42']),
    },
  },
  {
    id: 'schiefer',
    name: 'Schiefer',
    description: 'Modernes, ruhiges Dunkelgrau – dezent und edel.',
    tokens: {
      '--bg': 'radial-gradient(1100px 700px at 70% -10%, #2a2f3a, #14171d)',
      '--fg': '#eef1f6',
      '--muted': '#a6adba',
      '--surface': '#1c2029',
      '--surface-2': '#262b36',
      '--border': 'rgba(255,255,255,0.10)',
      '--accent': '#f59e0b',
      '--accent-fg': '#1b1206',
      '--correct': '#34d399',
      '--wrong': '#f87171',
      '--radius': '0.8rem',
      '--opt-fg': '#ffffff',
      '--font-display': "'Segoe UI', system-ui, sans-serif",
      '--font-body': "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      ...opt(['#f59e0b', '#3b82f6', '#10b981', '#ef4444', '#8b5cf6', '#14b8a6']),
    },
  },
];

export const DEFAULT_THEME_ID = 'amt';

export function getTheme(id: string | null | undefined): Theme {
  return THEMES.find((t) => t.id === id) ?? THEMES[0]!;
}

/** Flache Token-Map inkl. optionaler Overrides (eigene Farben/Logo pro Quiz). */
export function themeVars(id: string | null | undefined, overrides?: Record<string, string> | null): Record<string, string> {
  return { ...getTheme(id).tokens, ...(overrides ?? {}) };
}
